// pages/Terms.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../global.css";
import "../styles/faqPage.css";
import ReviewsBar from "../components/ReviewsBar";


const Terms = () => {
  const siteMode = process.env.REACT_APP_SITE_MODE || "ccg";
  const isPlanner = siteMode === "planner";
  const isMinibusGrab = siteMode === "minibusgrab";
  const isCoachGrab = siteMode === "cheapcoachgrab";
  const brandName = isMinibusGrab ? "MinibusGrab" : isCoachGrab ? "CheapCoachGrab" : "CheapCabGrab";
  const brandDomain = isMinibusGrab ? "www.minibusgrab.com" : isCoachGrab ? "www.cheapcoachgrab.com" : "www.cheapcabgrab.com";
  const brandService = isMinibusGrab
    ? "minibus hire, airport transfers and group travel"
    : isCoachGrab
      ? "coach hire, airport transfers, events and large group travel"
      : "taxi, minibus and coach services";
  const isPartnerBrand = isMinibusGrab || isCoachGrab;

  if (isPlanner) {
    return (
      <div className="faq-page">
        <Helmet>
          <title>Terms – Airport Journey Planner</title>
          <meta
            name="description"
            content="Airport Journey Planner terms covering live travel intelligence, planner estimates, data limitations and user responsibilities."
          />
          <link rel="canonical" href="https://www.airportjourneyplanner.com/terms" />
          <meta property="og:title" content="Airport Journey Planner – Terms" />
          <meta
            property="og:description"
            content="Terms for using Airport Journey Planner live road, rail, flight and airport journey tools."
          />
          <meta property="og:url" content="https://www.airportjourneyplanner.com/terms" />
        </Helmet>

        <div className="faq-grid">
          <div className="faq-left">
            <header className="faq-header">
              <h1>Terms</h1>
              <p>
                Airport Journey Planner provides live airport journey intelligence and planning
                estimates.
              </p>
            </header>

            <div className="faq-section">
              <h2>1. Service Purpose</h2>
              <p>
                Airport Journey Planner helps users understand road, rail, flight, weather and
                airport conditions before travelling. Results are planning guidance, not a guarantee
                of journey time, flight operation, queue time or airport access.
              </p>
            </div>

            <div className="faq-section">
              <h2>2. Live Data and Accuracy</h2>
              <p>
                We use multiple live and scheduled data sources. These may be delayed, incomplete,
                unavailable, changed by source providers, or affected by incidents outside our control.
                We show deterministic summaries where possible, but you should still check official
                airline, airport, rail and road sources before travel.
              </p>
            </div>

            <div className="faq-section">
              <h2>3. Planner Outputs</h2>
              <p>
                When you enter a postcode, airport, date/time or flight number, the planner may estimate
                when to leave, routes, buffers and live risks. You remain responsible for deciding when
                to travel and allowing suitable contingency.
              </p>
            </div>

            <div className="faq-section">
              <h2>4. No Transport Booking Contract</h2>
              <p>
                Airport Journey Planner pages are information and planning tools. Unless a separate
                booking/payment flow is completed, using the planner does not create a taxi, transfer,
                rail, parking or airport service booking.
              </p>
            </div>

            <div className="faq-section">
              <h2>5. Availability</h2>
              <p>
                We may update, suspend or remove planner features, supported airports, routes or live
                modules as data coverage changes. Heathrow and East Midlands Airport are currently the
                primary supported airports.
              </p>
            </div>

            <div className="faq-section">
              <h2>6. Liability</h2>
              <p>
                We are not liable for missed flights, missed connections, delay costs, parking charges,
                rail disruption, road closures, airport queue changes or decisions made solely from
                planner output. Use the tool as guidance alongside official sources.
              </p>
            </div>

            <div className="faq-section">
              <h2>7. Contact</h2>
              <p>
                For support, corrections or data feedback, email{" "}
                <a className="faq-link" href="mailto:support@airportjourneyplanner.com">
                  support@airportjourneyplanner.com
                </a>.
              </p>
              <p><strong>Last updated:</strong> May 2026</p>
            </div>

            <section className="faq-section" style={{ marginTop: "35px" }}>
              <h2>Useful Links</h2>
              <ul>
                <li><Link className="faq-link" to="/">Airport Journey Planner</Link></li>
                <li><Link className="faq-link" to="/heathrow">Heathrow Planner</Link></li>
                <li><Link className="faq-link" to="/east-midlands-airport">East Midlands Airport Planner</Link></li>
                <li><Link className="faq-link" to="/privacy">Privacy Policy</Link></li>
                <li><Link className="faq-link" to="/contact">Contact</Link></li>
              </ul>
            </section>
          </div>

          <aside className="faq-right">
            <div className="faq-reviews-card">
              <h2>Airport journey intelligence</h2>
              <p>Live planning guidance for road, rail, flight and airport conditions.</p>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-page">
      {/* ======================================================
          ⭐ SEO BLOCK — strong for rankings
      ======================================================= */}
      <Helmet>
        <title>Terms & Conditions – {brandName}</title>
        <meta
          name="description"
          content={
            isPartnerBrand
              ? `Read the ${brandName} Terms & Conditions covering ${brandService}, bookings, cancellations, airport pickups, refunds, driver standards, insurance and partner operators.`
              : "Read the official CheapCabGrab Terms & Conditions covering bookings, cancellations, airport pickups, refunds, driver standards, insurance, public holidays and more."
          }
        />
        <link rel="canonical" href={`https://${brandDomain}/terms`} />

        <meta
          name="keywords"
          content={
            isMinibusGrab
              ? "MinibusGrab terms, minibus hire terms, airport minibus terms, cancellation policy, refund policy"
              : isCoachGrab
                ? "CheapCoachGrab terms, coach hire terms, airport coach terms, cancellation policy, refund policy"
                : "taxi terms, private hire terms, CheapCabGrab terms, cancellation policy, refund policy, airport taxi terms, minibus hire terms"
          }
        />

        <meta property="og:title" content={`${brandName} – Terms & Conditions`} />
        <meta
          property="og:description"
          content={`Official ${brandName} Terms & Conditions for ${brandService}. Clear rules for refunds, cancellations and airport transfers.`}
        />
        <meta property="og:url" content={`https://${brandDomain}/terms`} />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TermsOfService",
            name: `${brandName} Terms & Conditions`,
            url: `https://${brandDomain}/terms`,
            provider: {
              "@type": "Organization",
              name: isPartnerBrand ? "CheapCabGrab" : brandName,
              url: isPartnerBrand ? "https://www.cheapcabgrab.com" : `https://${brandDomain}`,
            },
          })}
        </script>
      </Helmet>

      {/* ======================================================
          ⭐ TWO-COLUMN GRID LAYOUT (same as FAQ & Contact)
      ======================================================= */}
      <div className="faq-grid">
        {/* LEFT SIDE */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>{isPartnerBrand ? "Terms & Conditions" : "📄 Terms & Conditions"}</h1>
            {isPartnerBrand ? (
              <p>
                Please read these terms carefully before booking with{" "}
                <strong>{brandName}</strong>. {brandName} is a brand front door for group
                transport enquiries and bookings handled through the CheapCabGrab platform.
                These terms exist to protect both passengers and drivers and ensure safe,
                reliable transport.
              </p>
            ) : (
              <p>
                Please read these terms carefully before booking with{" "}
                <strong>CheapCabGrab</strong>.  
                These terms exist to protect both passengers and drivers and ensure safe,
                reliable transport.
              </p>
            )}
          </header>

          {/* ======================================================
               ⭐ TERMS SECTIONS
          ======================================================= */}

          <div className="faq-section">
            <h2>1. Contractual Relationship</h2>
            {isPartnerBrand ? (
              <>
                <p>
                  {brandName} enquiries and bookings are processed through the CheapCabGrab
                  platform. Fulfilment may be by CheapCabGrab or approved licensed partner
                  operators, including RHP Minibus Hire UK Ltd where larger vehicles are
                  required. When you place a booking, the contract for the journey is with
                  the operator confirmed for that booking. All drivers and partner operators
                  are expected to be:
                </p>
                <ul>
                  <li>Fully licensed for the vehicle and journey type</li>
                  <li>Insured for hire and reward</li>
                  <li>Checked or monitored for safety and professionalism</li>
                </ul>
              </>
            ) : (
              <>
                <p>
                  CheapCabGrab operates as a licensed private hire operator under relevant
                  UK council regulations. When you place a booking, the contract for the
                  journey is between you and CheapCabGrab. All drivers are:
                </p>
                <ul>
                  <li>✔ Fully licensed for private hire</li>
                  <li>✔ DBS checked</li>
                  <li>✔ Insured for hire & reward</li>
                  <li>✔ Monitored for safety and professionalism</li>
                </ul>
              </>
            )}
          </div>

          <div className="faq-section">
            <h2>2. Booking Requests</h2>
            <p>
              Advance bookings are recommended to ensure availability.  
              Same-day bookings are accepted depending on driver availability.
            </p>
          </div>

          <div className="faq-section">
            <h2>Quotes, Estimates and Availability</h2>
            <p>
              Enquiry-only journeys, larger vehicles, event-day journeys, airport journeys and
              partner-operator allocations are estimates until confirmed by our team. Vehicle type,
              availability and final fare may vary depending on driver availability, passenger numbers,
              luggage, route conditions, timing and operational demand.
            </p>
            <p>
              For instant online bookings, the fare is fixed once payment is completed and the booking is
              accepted. In rare cases where we are unable to fulfil a confirmed booking due to driver illness,
              vehicle failure, licensing or operational issues outside our control, we will contact you as soon
              as possible and provide either a suitable alternative or a full refund for the affected journey.
            </p>
          </div>

          <div className="faq-section">
            <h2>3. Cancellations</h2>
            <p>
              Cancellation requests must be emailed to{" "}
              <a className="faq-link" href="mailto:support@cheapcabgrab.com">
                support@cheapcabgrab.com
              </a>{" "}
              and confirmed by our team.
            </p>
            <ul>
              <li>🟢 24+ hours → full refund minus £10 admin fee</li>
              <li>🔴 Under 24 hours → full fare retained</li>
              <li>ℹ️ Changes within 12 hours may be chargeable</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>4. Delays & Route</h2>
            <p>
              {isPartnerBrand ? brandName : "CheapCabGrab"} is not responsible for delays caused by traffic, road closures,
              weather, or incidents.  
              Drivers will take the most appropriate route on the day unless you request
              otherwise.
            </p>
          </div>

          <div className="faq-section">
            <h2>5. Insurance & Liability</h2>
            <p>
              Vehicles are insured for hire & reward.  
              Customers are responsible for their personal belongings and should check
              travel insurance for coverage.
            </p>
          </div>

          <div className="faq-section">
            <h2>6. Passenger Behaviour</h2>
            <p>
              Drivers may refuse travel to anyone who:
            </p>
            <ul>
              <li>❌ Is intoxicated</li>
              <li>❌ Behaves aggressively</li>
              <li>❌ Poses a safety risk</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>7. Luggage Policy</h2>
            <p>
              Standard luggage is allowed. Excess, oversized or unsafe items may be refused
              if they compromise safety or available space.
            </p>
          </div>

          <div className="faq-section">
            <h2>8. No-Show Policy</h2>
            <p>
              If the passenger leaves the pickup point without contacting {isPartnerBrand ? brandName : "CheapCabGrab"},
              the journey will be marked as a no-show with no refund due.
            </p>
          </div>

          <div className="faq-section">
            <h2>9. Vehicle Damage</h2>
            <p>
              Passengers are liable for any damage caused to the vehicle interior.
            </p>
          </div>

          <div className="faq-section">
            <h2>10. Additional Charges</h2>
            <p>May apply for:</p>
            <ul>
              <li>🕒 Waiting time</li>
              <li>🅿️ Parking fees</li>
              <li>➕ Extra stops</li>
              <li>👶 Baby seats</li>
              <li>📟 Meet & greet requests</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>11. Grace Period</h2>
            <p>
              • Standard pickups → 15 minutes  
              • Airport pickups → 45 minutes (from car park time)  
            </p>
          </div>

          <div className="faq-section">
            <h2>12. Public Holidays</h2>
            <p>
              Christmas Day, Boxing Day & New Year periods are charged at 1.5× standard
              fare.
            </p>
          </div>

          <div className="faq-section">
            <h2>13. Affiliates & Partner Operators</h2>
            <p>
              {isPartnerBrand ? brandName : "CheapCabGrab"} bookings may be allocated to approved affiliate operators (including PSV
              partners such as{" "}
              <strong>RHP Minibus Hire UK Ltd</strong>) during peak demand or where large
              vehicles are required.
            </p>
          </div>

          {/* ===========================================
                ⭐ REFUNDS SECTION
          ============================================ */}
          <div className="faq-section">
            <h2>Refunds & Payment Disputes</h2>

            <ul>
              <li>
                <strong>Full refund</strong> → 24+ hours notice (minus £10 processing fee)
              </li>

              <li>
                <strong>No refund</strong> → under 24 hours or no-show
              </li>

              <li>
                <strong>Processing fees:</strong> Non-refundable Stripe fees deducted
              </li>

              <li>
                <strong>Refund speed:</strong> 5–10 days depending on bank
              </li>

              <li>
                <strong>Disputes:</strong> Passenger must contact {isPartnerBrand ? brandName : "CheapCabGrab"} before any
                chargeback
              </li>
            </ul>

            <p>
              Our policies ensure fairness for both passengers and driver partners.
            </p>
          </div>

          <p><strong>Last updated:</strong> October 2025</p>

          <section className="faq-section" style={{ marginTop: "35px" }}>
            <h2>Useful Links</h2>
            <ul>
              <li><Link className="faq-link" to="/">{isPartnerBrand ? `${brandName} Home` : "Instant Taxi Quote"}</Link></li>
              {!isMinibusGrab && !isCoachGrab ? <li><Link className="faq-link" to="/heathrow-taxi">Heathrow Taxi</Link></li> : null}
              {!isCoachGrab ? <li><Link className="faq-link" to="/minibus-hire">Minibus Hire</Link></li> : null}
              {!isMinibusGrab ? <li><Link className="faq-link" to="/coach-hire">Coach Hire</Link></li> : null}
              <li><Link className="faq-link" to="/contact">Contact Us</Link></li>
            </ul>
          </section>
        </div>

        {/* RIGHT SIDE */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>{brandName} terms</h2>
            <p>
              {isPartnerBrand
                ? "Group transport brand pages backed by CheapCabGrab operations and approved partner operators."
                : "Trusted by thousands across Heathrow, Slough and London."}
            </p>
            <div style={{ marginTop: "10px" }}>
              <ReviewsBar />
            </div>
          </div>
        </aside>
      </div>

      {/* Floating CTA */}
      <button
        className="floating-book-btn"
        onClick={() => (window.location.href = "/")}
      >
        {isMinibusGrab ? "Get Minibus Quote" : isCoachGrab ? "Get Coach Quote" : "🚖 Book Now"}
      </button>
    </div>
  );
};

export default Terms;
